import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'
import { Plus, Pencil, Trash2, X, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

/** Espelha CAMPOS_REGRA_IMPOSTO (fiscal/models.py) e CAMPOS_REGRA (NotaAvulsa).
 *  [campo, label, tipo, placeholder, dica] */
const GRUPOS: [string, [string, string, 'num' | 'txt' | 'origem' | 'modbc', string, string?][]][] = [
  ['ICMS', [
    ['origem', 'Origem da mercadoria', 'origem', ''],
    ['cst', 'CST ICMS', 'txt', '00', 'Vale no Regime Normal'],
    ['csosn', 'CSOSN', 'txt', '102', 'Vale no Simples Nacional'],
    ['aliquota_icms', '% ICMS', 'num', '18'],
    ['reducao_bc_percent', '% Redução de BC', 'num', '0'],
  ]],
  ['ICMS-ST', [
    ['modalidade_bc_st', 'Modalidade da BC ST', 'modbc', ''],
    ['mva_st', '% MVA', 'num', '0'],
    ['reducao_bc_st_percent', '% Redução BC ST', 'num', '0'],
    ['aliquota_icms_st', '% ICMS ST', 'num', '0'],
  ]],
  ['FCP', [
    ['p_fcp', '% FCP', 'num', '0'],
    ['p_fcp_st', '% FCP ST', 'num', '0'],
    ['codigo_beneficio', 'Cód. benefício fiscal', 'txt', '—'],
  ]],
  ['PIS / COFINS', [
    ['cst_pis', 'CST PIS', 'txt', '49'],
    ['aliquota_pis', '% PIS', 'num', '0'],
    ['cst_cofins', 'CST COFINS', 'txt', '49'],
    ['aliquota_cofins', '% COFINS', 'num', '0'],
  ]],
  ['IPI', [
    ['ipi_cst', 'CST IPI', 'txt', '99'],
    ['ipi_aliquota', '% IPI', 'num', '0'],
    ['ipi_codigo_enquadramento', 'Cód. enquadramento', 'txt', '999'],
  ]],
  ['IBS / CBS (Reforma Tributária)', [
    ['cst_ibs_cbs', 'CST IBS/CBS', 'txt', '000'],
    ['c_class_trib', 'cClassTrib', 'txt', '000001'],
    ['p_ibs_uf', '% IBS UF', 'num', '0,1000'],
    ['p_ibs_mun', '% IBS Município', 'num', '0,0000'],
    ['p_cbs', '% CBS', 'num', '0,9000'],
  ]],
  ['Extras', [
    ['informacoes_adicionais', 'Informação adicional do item', 'txt', ''],
  ]],
]

const ORIGENS = [
  ['0', '0 - Nacional'], ['1', '1 - Estrangeira (importação direta)'],
  ['2', '2 - Estrangeira (mercado interno)'], ['3', '3 - Nacional, +40% importação'],
  ['4', '4 - Nacional (processos básicos)'], ['5', '5 - Nacional, até 40% importação'],
  ['6', '6 - Estrangeira (importação direta, s/ similar)'],
  ['7', '7 - Estrangeira (mercado interno, s/ similar)'], ['8', '8 - Nacional, +70% importação'],
]
const MODALIDADES_BC_ST = [
  ['0', '0 - Preço tabelado'], ['1', '1 - Lista negativa'], ['2', '2 - Lista positiva'],
  ['3', '3 - Lista neutra'], ['4', '4 - Margem Valor Agregado (MVA)'], ['5', '5 - Pauta'],
]

const VAZIA: any = {
  id: 0, nome: '', descricao: '', ativo: true, ordem: 0,
  filial_id: null, crt: null, uf_destino: null, cfops: [],
}

export default function RegrasImposto() {
  const qc = useQueryClient()
  const [modal, setModal] = useState<any | null>(null)
  const [cfopsTexto, setCfopsTexto] = useState('')

  const { data: regras = [], isLoading } = useQuery<any[]>({
    queryKey: ['regras-imposto-cad'],
    queryFn: async () =>
      (await api.get('/regras-imposto', { params: { apenas_ativas: false } })).data.regras,
  })

  const { data: filiais = [] } = useQuery<any[]>({
    queryKey: ['filiais'],
    queryFn: async () => {
      const d = (await api.get('/filiais')).data
      return Array.isArray(d) ? d : (d.filiais || [])
    },
  })

  const { data: cfops = [] } = useQuery<any[]>({
    queryKey: ['cfops-regras-imposto'],
    queryFn: async () => (await api.get('/fiscal/cfops')).data,
  })

  const salvar = useMutation({
    mutationFn: async (r: any) => {
      const { id, qtd_campos, criado_em, ...body } = r
      body.cfops = cfopsTexto.split(/[,;\s]+/).map((c: string) => c.replace(/\D/g, ''))
        .filter(Boolean)
      return id
        ? (await api.put(`/regras-imposto/${id}`, body)).data
        : (await api.post('/regras-imposto', body)).data
    },
    onSuccess: () => {
      toast.success('Regra salva')
      setModal(null)
      qc.invalidateQueries({ queryKey: ['regras-imposto-cad'] })
      // A nota avulsa consulta por CFOP com outra chave — invalida as duas.
      qc.invalidateQueries({ queryKey: ['regras-imposto'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const excluir = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/regras-imposto/${id}`)).data,
    onSuccess: () => {
      toast.success('Regra excluída')
      qc.invalidateQueries({ queryKey: ['regras-imposto-cad'] })
      qc.invalidateQueries({ queryKey: ['regras-imposto'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao excluir'),
  })

  const abrir = (r: any) => {
    setModal({ ...VAZIA, ...r })
    setCfopsTexto((r.cfops || []).join(', '))
  }

  const setC = (campo: string, v: any) => setModal((m: any) => ({ ...m, [campo]: v }))

  // cfopsTexto é a fonte única; a lista marcável só edita esse texto. Dois
  // estados paralelos dessincronizariam na primeira edição manual.
  const cfopsSel = cfopsTexto.split(/[,;\s]+/).map(c => c.replace(/\D/g, '')).filter(Boolean)
  const alternarCfop = (codigo: string) => {
    const cod = String(codigo).replace(/\D/g, '')
    const novo = cfopsSel.includes(cod)
      ? cfopsSel.filter(c => c !== cod)
      : [...cfopsSel, cod]
    setCfopsTexto(novo.join(', '))
  }
  // Vazio = herda do produto. Zero = aplica zero. São coisas diferentes, e é por
  // isso que string vazia vira null em vez de 0.
  const definido = (campo: string) =>
    modal && modal[campo] !== null && modal[campo] !== undefined && modal[campo] !== ''

  const campo = ([nome, label, tipo, ph, dica]: any) => {
    const marcado = definido(nome)
    const cls = `input ${marcado ? 'ring-1 ring-brand-400' : ''}`
    const onCh = (v: string) => setC(nome, v === '' ? null : v)
    return (
      <div key={nome}>
        <label className="label" title={dica}>
          {label}
          {marcado && <span className="ml-1 text-[10px] text-brand-600">definido</span>}
        </label>
        {tipo === 'origem' ? (
          <select className={cls} value={modal[nome] ?? ''} onChange={e => onCh(e.target.value)}>
            <option value="">— herda do produto —</option>
            {ORIGENS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ) : tipo === 'modbc' ? (
          <select className={cls} value={modal[nome] ?? ''} onChange={e => onCh(e.target.value)}>
            <option value="">— herda do produto —</option>
            {MODALIDADES_BC_ST.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ) : (
          <input className={cls} type={tipo === 'num' ? 'number' : 'text'}
            step={tipo === 'num' ? '0.0001' : undefined}
            placeholder={ph ? `${ph} — ou vazio p/ herdar` : 'herda do produto'}
            value={modal[nome] ?? ''} onChange={e => onCh(e.target.value)} />
        )}
      </div>
    )
  }

  const escopo = (r: any) => {
    const p = []
    if (r.filial_id) p.push(filiais.find((f: any) => f.id === r.filial_id)?.nome_fantasia || `Filial ${r.filial_id}`)
    if (r.crt) p.push(r.crt === '3' ? 'Regime Normal' : 'Simples')
    if (r.uf_destino) p.push(`destino ${r.uf_destino}`)
    return p.length ? p.join(' · ') : 'Todas'
  }

  return (
    <div className="p-4 md:p-6 w-full max-w-7xl mx-auto">
      {/* flex-wrap + gap: sem isso o botão quebra em duas linhas e o texto passa
          por baixo dele. shrink-0 mantém o botão inteiro em tela estreita. */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold">Regras de Imposto</h2>
          <p className="text-sm text-ink-500 max-w-2xl">
            Perfil fiscal vinculado a CFOPs. Escolhida na nota avulsa, preenche a
            tributação de todos os itens — inclusive dos componentes de kit.
          </p>
        </div>
        <button onClick={() => abrir(VAZIA)} className="btn-primary shrink-0 whitespace-nowrap">
          <Plus size={14} /> Nova regra
        </button>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead><tr>
            <th>Regra</th><th>CFOPs</th>
            <th className="hidden lg:table-cell">Escopo</th>
            <th className="hidden md:table-cell">Campos</th>
            <th>Status</th><th className="text-right">Ações</th>
          </tr></thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="text-center py-8">Carregando...</td></tr>}
            {!isLoading && regras.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-ink-500">
                Nenhuma regra cadastrada.
              </td></tr>
            )}
            {regras.map((r: any) => (
              <tr key={r.id} className="hover:bg-violet-50">
                <td>
                  <div className="font-medium">{r.nome}</div>
                  {r.descricao && <div className="text-xs text-ink-500">{r.descricao}</div>}
                </td>
                <td className="text-xs">
                  {r.cfops?.length
                    ? r.cfops.join(', ')
                    : <span className="text-amber-700">sem CFOP — não aparece na nota</span>}
                </td>
                <td className="text-xs hidden lg:table-cell">{escopo(r)}</td>
                <td className="text-xs hidden md:table-cell">
                  {r.qtd_campos
                    ? `${r.qtd_campos} campo(s)`
                    : <span className="text-amber-700">vazia</span>}
                </td>
                <td>{r.ativo
                  ? <span className="badge-success">Ativa</span>
                  : <span className="badge-info">Inativa</span>}</td>
                <td className="text-right whitespace-nowrap">
                  <button onClick={() => abrir(r)} className="p-1.5 rounded hover:bg-ink-100 text-ink-600" title="Editar"><Pencil size={14} /></button>
                  <button onClick={() => pedirExclusao({
                    descricao: `a regra "${r.nome}"`,
                    onConfirm: () => excluir.mutate(r.id),
                  })} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Excluir"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[95vh] md:max-h-[90vh] flex flex-col">
            <div className="px-5 py-4 border-b shrink-0 flex items-center justify-between">
              <h3 className="font-semibold text-lg">
                {modal.id ? 'Editar regra' : 'Nova regra de imposto'}
              </h3>
              <button onClick={() => setModal(null)} className="text-ink-400 hover:text-ink-700">
                <X size={18} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
              <div className="text-xs rounded-lg px-3 py-2 border bg-brand-50 border-brand-200 text-ink-700">
                <strong>Campo vazio = herda do produto. Campo com 0 = zera o imposto.</strong>{' '}
                Preencha só o que esta operação muda — o resto continua vindo do cadastro
                de cada produto.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <label className="label">Nome da regra</label>
                  <input className="input" autoFocus value={modal.nome}
                    placeholder="Ex: Remessa para conserto, Bonificação, Devolução de venda"
                    onChange={e => setC('nome', e.target.value)} />
                </div>
                <div>
                  <label className="label">Ordem na lista</label>
                  <input className="input" type="number" value={modal.ordem ?? 0}
                    onChange={e => setC('ordem', Number(e.target.value) || 0)} />
                </div>
                <div className="md:col-span-3">
                  <label className="label">Descrição (opcional)</label>
                  <input className="input" value={modal.descricao || ''}
                    onChange={e => setC('descricao', e.target.value)} />
                </div>
                <div className="md:col-span-3">
                  <label className="label">CFOPs em que esta regra aparece</label>
                  {/* Clique marca/desmarca; o campo de texto continua valendo pra
                      CFOP ainda não cadastrado — travar nisso impediria salvar. */}
                  <div className="border rounded-lg p-2 max-h-40 overflow-y-auto grid grid-cols-2 md:grid-cols-3 gap-1">
                    {cfops.length === 0 && (
                      <p className="text-xs text-ink-500 col-span-full px-1 py-2">
                        Nenhum CFOP cadastrado — cadastre em Fiscal → CFOP, ou digite abaixo.
                      </p>
                    )}
                    {cfops.map((c: any) => {
                      const marcado = cfopsSel.includes(c.codigo)
                      return (
                        <button key={c.id} type="button" onClick={() => alternarCfop(c.codigo)}
                          className={`text-left text-xs px-2 py-1 rounded truncate ${marcado
                            ? 'bg-brand-100 text-brand-800 font-medium'
                            : 'hover:bg-ink-100 text-ink-600'}`}
                          title={`${c.codigo} — ${c.descricao}`}>
                          {marcado ? '✓ ' : ''}{c.codigo} — {c.descricao}
                        </button>
                      )
                    })}
                  </div>
                  <input className="input mt-2" value={cfopsTexto}
                    placeholder="5915, 6915"
                    onChange={e => setCfopsTexto(e.target.value)} />
                  <p className="text-[11px] text-ink-500 mt-0.5">
                    {cfopsSel.length
                      ? `${cfopsSel.length} CFOP(s) selecionado(s).`
                      : 'Sem CFOP, a regra não aparece no seletor da nota.'}
                    {' '}Lembre do par interna/interestadual (5xxx e 6xxx) — a regra do
                    5102 não vale sozinha numa venda para outro estado.
                  </p>
                </div>
              </div>

              <div className="border-t pt-3">
                <div className="text-sm font-semibold text-ink-800 mb-2">
                  Onde vale
                  <span className="font-normal text-xs text-ink-500 ml-2">
                    (em branco = serve a qualquer um)
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="label">Filial emitente</label>
                    <select className="input" value={modal.filial_id ?? ''}
                      onChange={e => setC('filial_id', e.target.value ? Number(e.target.value) : null)}>
                      <option value="">Todas as filiais</option>
                      {filiais.map((f: any) => (
                        <option key={f.id} value={f.id}>{f.nome_fantasia || f.razao_social}</option>
                      ))}
                    </select>
                    {/* Produto é da empresa, não de uma loja — por isso regra com
                        filial não pode ser vinculada a produto. Sem este aviso, o
                        contador cria a regra, não a encontra na lista do produto
                        e não entende o motivo. */}
                    {modal.filial_id && (
                      <p className="text-[11px] text-amber-600 mt-0.5">
                        Regra de uma filial só pode ser usada na nota. Para vincular
                        a produtos, deixe em "Todas as filiais".
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="label">Regime (CRT)</label>
                    <select className="input" value={modal.crt ?? ''}
                      onChange={e => setC('crt', e.target.value || null)}>
                      <option value="">Qualquer regime</option>
                      <option value="1">Simples Nacional</option>
                      <option value="2">Simples — excesso de sublimite</option>
                      <option value="3">Regime Normal</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">UF de destino</label>
                    <input className="input" maxLength={2} value={modal.uf_destino ?? ''}
                      placeholder="Qualquer"
                      onChange={e => setC('uf_destino', e.target.value.toUpperCase() || null)} />
                  </div>
                </div>
              </div>

              {GRUPOS.map(([titulo, campos]) => (
                <div key={titulo} className="border-t pt-3">
                  <div className="text-sm font-semibold text-ink-800 mb-2">{titulo}</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {campos.map(campo)}
                  </div>
                </div>
              ))}

              <label className="flex items-center gap-2 text-sm border-t pt-3">
                <input type="checkbox" checked={!!modal.ativo}
                  onChange={e => setC('ativo', e.target.checked)} />
                Ativa (aparece no seletor da nota avulsa)
              </label>
            </div>

            <div className="px-5 py-4 border-t shrink-0 flex gap-2 justify-end">
              <button onClick={() => setModal(null)} className="btn-secondary">Cancelar</button>
              <button onClick={() => salvar.mutate(modal)}
                disabled={!modal.nome?.trim() || salvar.isPending} className="btn-primary">
                {salvar.isPending ? <Loader2 size={14} className="animate-spin" /> : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}